// @react-compiler-disable
import {
  Dtos_V1_ExtraSystemDto,
  Input_V2_Mutations_RaptorJob_CreateRaptorJobMutationInput,
  Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput
} from '@/common/infrastructure/gql/generated/schema';
import { JobDetails, JobFormData } from '../types/JobTypes';
import { normalizeCallerId } from '@/common/application/utils/stringUtils';
import { dateTimeToTimeString, formatDate, normalizeTimeField, stringTimeToDate } from '@/common/application/utils/timeUtils';
import {
  detectChangeTypes,
  mapClearableField,
  mapClearableStringField,
  mapCrewOnlyUpdate,
  mapFlagOnlyUpdate,
  mapFullUpdate,
  validateDateField
} from './jobUpdateMappers.helpers';

export interface CurrentUserInfo {
  id: number;
  name: string;
}

export const mapJobToFormData = (
  job?: Partial<JobDetails> | null,
  availableExtraSystems?: Dtos_V1_ExtraSystemDto[],
  currentUser?: CurrentUserInfo | null
): JobFormData => {
  if (!job) {
    return {
      id: undefined,

      // Customer Information
      customerId: undefined,
      callerId: undefined,
      customerPhone: '',
      customerEmail: '',
      // Job Information
      jobName: '',
      jobSiteId: undefined,
      typeId: undefined,
      sizeRequestedId: undefined,
      dispatchLocationId: undefined,
      jobInformationId: undefined,
      jobStatusId: undefined,
      cod: false,

      // Order and Dispatch Information
      // Auto-populate dispatcher with current user's dispatcher employee record (if found)
      // currentUser.id contains the dispatcherId when a matching dispatcher was found
      dispatcherId: currentUser?.id,
      salesAgentId: undefined,
      vendorId: undefined,
      poNumber: '',

      // Special Flags
      specialFlag1Id: undefined,
      specialFlag2Id: undefined,
      specialFlag3Id: undefined,

      // Date and Time Information
      startDate: undefined,
      startTime: undefined,
      onJobTime: undefined,
      leaveTime: undefined,
      showUpTime: undefined,
      estimatedHours: undefined,
      timeZone: '',

      // Notes
      dispatchNotes: '',
      notesForCrew: '',

      // Resources
      equipment: [],
      equipmentsToAdd: [],
      equipmentsToDelete: [],

      crew: [],
      crewMembersToAdd: [],
      crewMembersToDelete: [],

      extraSystems: [],
      extraSystemsToAdd: [],
      extraSystemsToDelete: []
    };
  }

  // Enrich extra systems with complete information from availableExtraSystems
  // Create a lookup map for O(n) performance instead of O(n²) with find()
  const availableExtraSystemsMap =
    availableExtraSystems?.reduce(
      (acc, system) => {
        acc[system.id] = system;
        return acc;
      },
      {} as Record<string, Dtos_V1_ExtraSystemDto>
    ) ?? {};

  const enrichedExtraSystems =
    job.extraSystems?.map((extraSystem) => {
      const availableExtraSystem = availableExtraSystemsMap[extraSystem.id];
      return {
        ...extraSystem,
        name: availableExtraSystem?.name ?? extraSystem.name,
        description: availableExtraSystem?.description ?? extraSystem.description,
        price: availableExtraSystem?.price ?? extraSystem.price,
        quantity: availableExtraSystem?.quantity ?? extraSystem.quantity,
        class: availableExtraSystem?.class ?? extraSystem.class,
        billable: availableExtraSystem?.billable ?? extraSystem.billable,
        taxable: availableExtraSystem?.taxable ?? extraSystem.taxable,
        defaultItem: availableExtraSystem?.defaultItem ?? extraSystem.defaultItem,
        showAsExtraSystemOnJob: availableExtraSystem?.showAsExtraSystemOnJob ?? extraSystem.showAsExtraSystemOnJob
      };
    }) ?? [];

  return {
    id: job.id,

    // Customer Information
    customerId: job.customer?.id ?? undefined,
    callerId: undefined, // callerId is not stored in job details, it's form-specific
    customerPhone: job.customer?.primaryPhone ?? '',
    customerEmail: job.customer?.primaryEmail ?? '',
    // Job Information
    jobName: job.jobName ?? '',
    jobSiteId: job.jobSite?.id ?? undefined,
    typeId: job.type?.id ?? undefined,
    sizeRequestedId: job.sizeRequested?.id ?? undefined,
    dispatchLocationId: job.dispatchLocation?.id ?? undefined,
    jobInformationId: job.jobInformation?.id ?? undefined,
    jobStatusId: job.status?.id ?? undefined,
    cod: job.cod ?? false,

    // Order and Dispatch Information
    // Keep existing dispatcher if present, otherwise default to current user's dispatcher employee record (if found)
    // currentUser.id contains the dispatcherId when a matching dispatcher was found
    dispatcherId: job.dispatcher?.id ?? currentUser?.id,
    salesAgentId: job.salesAgent?.id ?? undefined,
    vendorId: job.vendor?.id ?? undefined,
    poNumber: job.poNumber ?? '',

    // Special Flags
    specialFlag1Id: job.specialFlag1?.id ?? undefined,
    specialFlag2Id: job.specialFlag2?.id ?? undefined,
    specialFlag3Id: job.specialFlag3?.id ?? undefined,

    // Date and Time Information
    startDate: job.startDate ?? '',
    startTime: stringTimeToDate(job.equipment?.[0]?.plannedAllocation?.startTime),
    onJobTime: job.equipment?.[0]?.plannedAllocation?.onJobTime ?? '',
    leaveTime: job.equipment?.[0]?.plannedAllocation?.leaveTime ?? '',
    showUpTime: job.equipment?.[0]?.plannedAllocation?.showUpTime ?? '',
    estimatedHours: job.equipment?.[0]?.plannedAllocation?.estimatedHours ?? 0,
    timeZone: job.timeZone ?? '',

    // Notes
    dispatchNotes: job.dispatchNotes ?? '',
    notesForCrew: job.notesForCrew ?? '',

    // Resources
    equipment: job.equipment ?? [],
    equipmentsToAdd: [],

    crew: job.crew ?? [],
    crewMembersToAdd: [],

    extraSystems: enrichedExtraSystems,
    extraSystemsToAdd: [],
    extraSystemsToDelete: []
  };
};

export const mapFormDataToCreateInput = (formData: JobFormData): Input_V2_Mutations_RaptorJob_CreateRaptorJobMutationInput => {
  // jobInformationId defaults to 1 when not provided or invalid
  const getJobInformationId = (): number => {
    if (formData.jobInformationId !== undefined && typeof formData.jobInformationId === 'number' && formData.jobInformationId > 0) {
      return formData.jobInformationId;
    }
    return 1; // Default value
  };
  const callerId = normalizeCallerId(formData.callerId, { preserveUndefined: true });

  return {
    // Customer Information
    ...(formData.customerId && { customerId: formData.customerId }),
    ...(callerId !== undefined && { callerId }),
    // Job Information
    ...(formData.jobName && { jobName: formData.jobName }),
    ...(formData.jobSiteId && { jobSiteId: Number(formData.jobSiteId) }),
    ...(formData.typeId && { typeId: formData.typeId }),
    ...(formData.sizeRequestedId && { sizeRequestedId: formData.sizeRequestedId }),
    ...(formData.dispatchLocationId && { dispatchLocationId: formData.dispatchLocationId }),
    // jobInformationId is required by backend - always include it, defaulting to 1
    jobInformationId: getJobInformationId(),
    ...(formData.jobStatusId && { jobStatusId: formData.jobStatusId }),
    ...(formData.cod !== undefined && { cod: formData.cod }),

    // Order and Dispatch Information
    ...(formData.dispatcherId && { dispatcherId: Number(formData.dispatcherId) }),
    ...(formData.salesAgentId && { salesAgentId: Number(formData.salesAgentId) }),
    ...(formData.vendorId && { vendorId: formData.vendorId }),
    ...(formData.poNumber && { poNumber: formData.poNumber }),

    // Special Flags
    ...(formData.specialFlag1Id && { specialFlag1Id: formData.specialFlag1Id }),
    ...(formData.specialFlag2Id && { specialFlag2Id: formData.specialFlag2Id }),
    ...(formData.specialFlag3Id && { specialFlag3Id: formData.specialFlag3Id }),

    // Date and Time Information
    ...(formData.startDate && { startDate: formatDate(formData.startDate, 'yyyy-MM-dd') }),
    ...(formData.startTime && { startTime: dateTimeToTimeString(formData.startTime as string) }),
    ...(formData.onJobTime && { onJobTime: dateTimeToTimeString(formData.onJobTime as string) }),
    ...(formData.leaveTime && { leaveTime: dateTimeToTimeString(formData.leaveTime as string) }),
    ...(formData.showUpTime && { showUpTime: dateTimeToTimeString(formData.showUpTime as string) }),
    ...(formData.estimatedHours !== undefined && formData.estimatedHours !== null && { estimatedHours: formData.estimatedHours }),
    ...(formData.timeZone && { timeZone: formData.timeZone }),

    // Notes
    ...(formData.dispatchNotes && { dispatchNotes: formData.dispatchNotes }),
    ...(formData.notesForCrew && { notesForCrew: formData.notesForCrew }),

    // Resources
    ...(formData.equipmentsToAdd && formData.equipmentsToAdd.length > 0 && { equipmentToAdd: formData.equipmentsToAdd }),
    ...(formData.equipmentsToDelete && formData.equipmentsToDelete.length > 0 && { equipmentToDelete: formData.equipmentsToDelete }),
    // Only send crew assignments with a valid roleId (> 0)
    ...(formData.crewMembersToAdd &&
      formData.crewMembersToAdd.length > 0 && {
        crewMembersToAdd: formData.crewMembersToAdd.filter((cm) => typeof cm.roleId === 'number' && cm.roleId > 0)
      }),
    ...(formData.crewMembersToDelete && formData.crewMembersToDelete.length > 0 && { crewMembersToDelete: formData.crewMembersToDelete }),
    ...(formData.extraSystemsToAdd && formData.extraSystemsToAdd.length > 0 && { extraSystemsToAdd: formData.extraSystemsToAdd }),
    ...(formData.extraSystemsToDelete &&
      formData.extraSystemsToDelete.length > 0 && { extraSystemsToDelete: formData.extraSystemsToDelete })
  };
};

export const mapFormDataToUpdateInput = (formData: JobFormData): Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput => {
  if (!formData.id) {
    throw new Error('Job ID is required for update');
  }
  const callerId = normalizeCallerId(formData.callerId, { preserveUndefined: true });
  return {
    id: formData.id,

    // Customer Information
    ...(formData.customerId && { customerId: formData.customerId }),
    ...(callerId !== undefined && { callerId }),
    // Job Information
    // Handle clearable string fields - send null when cleared
    ...(formData.jobName !== undefined && { jobName: mapClearableStringField(formData.jobName) }),
    ...(formData.jobSiteId && { jobSiteId: Number(formData.jobSiteId) }),
    ...(formData.typeId !== undefined && {
      typeId: formData.typeId === null || formData.typeId === 0 ? 0 : formData.typeId
    }),
    ...(formData.sizeRequestedId !== undefined && {
      sizeRequestedId: formData.sizeRequestedId === null || formData.sizeRequestedId === 0 ? 0 : formData.sizeRequestedId
    }),
    // Handle clearable fields - send 0 when cleared
    ...(formData.dispatchLocationId !== undefined && {
      dispatchLocationId: mapClearableField(formData.dispatchLocationId)
    }),
    ...(formData.jobInformationId !== undefined && {
      jobInformationId: mapClearableField(formData.jobInformationId)
    }),
    ...(formData.jobStatusId !== undefined && {
      jobStatusId: formData.jobStatusId === null || formData.jobStatusId === 0 ? 0 : formData.jobStatusId
    }),
    ...(formData.cod !== undefined && { cod: formData.cod }),

    // Order and Dispatch Information
    // Handle clearable fields - send 0 when cleared
    ...(formData.dispatcherId !== undefined && {
      dispatcherId: mapClearableField(formData.dispatcherId)
    }),
    ...(formData.salesAgentId !== undefined && {
      salesAgentId: mapClearableField(formData.salesAgentId)
    }),
    ...(formData.vendorId !== undefined && {
      vendorId: mapClearableField(formData.vendorId)
    }),
    // Handle clearable string fields - send null when cleared
    ...(formData.poNumber !== undefined && { poNumber: mapClearableStringField(formData.poNumber) }),

    // Special Flags
    // Handle clearable fields - send 0 when cleared
    ...(formData.specialFlag1Id !== undefined && {
      specialFlag1Id: mapClearableField(formData.specialFlag1Id)
    }),
    ...(formData.specialFlag2Id !== undefined && {
      specialFlag2Id: mapClearableField(formData.specialFlag2Id)
    }),
    ...(formData.specialFlag3Id !== undefined && {
      specialFlag3Id: mapClearableField(formData.specialFlag3Id)
    }),

    // Date and Time Information
    ...(formData.startDate !== undefined && { startDate: validateDateField(formData.startDate) }),
    // Time fields: Always include them (even as null) to allow clearing
    startTime: normalizeTimeField(formData.startTime),
    onJobTime: normalizeTimeField(formData.onJobTime),
    leaveTime: normalizeTimeField(formData.leaveTime),
    showUpTime: normalizeTimeField(formData.showUpTime),
    ...(formData.estimatedHours !== undefined && formData.estimatedHours !== null && { estimatedHours: formData.estimatedHours }),
    // Handle clearable string fields - send null when cleared
    ...(formData.timeZone !== undefined && { timeZone: mapClearableStringField(formData.timeZone) }),

    // Notes
    // Handle clearable string fields - send null when cleared
    ...(formData.dispatchNotes !== undefined && { dispatchNotes: mapClearableStringField(formData.dispatchNotes) }),
    ...(formData.notesForCrew !== undefined && { notesForCrew: mapClearableStringField(formData.notesForCrew) }),

    // Resources
    ...(formData.equipmentsToAdd && formData.equipmentsToAdd.length > 0 && { equipmentToAdd: formData.equipmentsToAdd }),
    ...(formData.equipmentsToDelete && formData.equipmentsToDelete.length > 0 && { equipmentToDelete: formData.equipmentsToDelete }),

    ...(formData.crewMembersToAdd && formData.crewMembersToAdd.length > 0 && { crewMembersToAdd: formData.crewMembersToAdd }),
    ...(formData.crewMembersToDelete && formData.crewMembersToDelete.length > 0 && { crewMembersToDelete: formData.crewMembersToDelete }),
    ...(formData.extraSystemsToAdd && formData.extraSystemsToAdd.length > 0 && { extraSystemsToAdd: formData.extraSystemsToAdd }),
    ...(formData.extraSystemsToDelete &&
      formData.extraSystemsToDelete.length > 0 && { extraSystemsToDelete: formData.extraSystemsToDelete })
  };
};

/**
 * Maps form data to update input excluding time fields (startTime, onJobTime, leaveTime, showUpTime).
 * Use this when updating only crew members or flags to avoid sending time information.
 *
 * CRITICAL: Only includes fields that are actually being changed (not just truthy values).
 * This prevents sending unnecessary fields like customerId, jobSiteId, etc. when only editing crew members or flags.
 */
export const mapFormDataToUpdateInputWithoutTimeFields = (
  formData: JobFormData
): Input_V2_Mutations_RaptorJob_UpdateRaptorJobMutationInput => {
  if (!formData.id) {
    throw new Error('Job ID is required for update');
  }

  // TypeScript now knows id is a number after the check above
  const jobId = formData.id;
  const formDataWithId = { ...formData, id: jobId } as JobFormData & { id: number };

  const { hasCrewChanges, hasFlagChanges, hasEquipmentChanges, hasExtraSystemsChanges } = detectChangeTypes(formData);

  // Early return: Crew-only changes
  if (hasCrewChanges && !hasFlagChanges && !hasEquipmentChanges && !hasExtraSystemsChanges) {
    return mapCrewOnlyUpdate(formDataWithId);
  }

  // Early return: Flag-only changes
  if (hasFlagChanges && !hasCrewChanges && !hasEquipmentChanges && !hasExtraSystemsChanges) {
    return mapFlagOnlyUpdate(formDataWithId);
  }

  // Fallback: Full update for mixed changes or other scenarios (excludes time fields)
  return mapFullUpdate(formDataWithId);
};
